import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, FormProvider } from 'react-hook-form'

import axios from '../../dataClient/dataClient'
import { applicantTypeMap, applicantTypeOptions } from '../../util/foiConsts'
import { BoldFormTitle } from '../styles/Text'
import { SpaceBetweenRowContainer, WhiteBGContainer, FlexEndButtonErrorContainer, FlexEndContainer, Form, FlexColumn } from '../styles/Containers'
import { ErrorMessage } from '../common/UserMessage'
import { SelectMenuItem } from '../styles/TextField'
import Button, { SecondaryButton } from '../common/Button'
import DetailSection from '../common/DetailSection'
import AutocompleteMultiSelect from '../inputFields/AutocompleteMultiSelect'
import FormSelect from '../inputFields/FormSelect'
import Keywords from './Keywords'
import Departments from './Departments'
import styled from '@emotion/styled'

const StyledBoldFormTitle = styled(BoldFormTitle)`
  line-height: 36px;
`
const HeadingContainer = styled(SpaceBetweenRowContainer)`
  align-items: flex-start;
`

const StyledFlexEndContainer = styled(FlexEndContainer)`
  flex-wrap: wrap;
`

const schema = yup.object().shape({
  applicantType: yup.string().required('Applicant type is required'),
  keywords: yup.array(),
  departments: yup.array().of(
    yup.object().shape({
      id: yup.string().required(),
      label: yup.string().required()
    })
  )
})

function mapDepartmentsToOptions(departments = [], allDepartments = []) {
  return departments.map(department => allDepartments.find(option => option.id === department.id) || department)
}

function getClassificationFormValues(classification, allDepartments) {
  return {
    applicantType: classification.applicantType,
    keywords: classification.keywords,
    departments: mapDepartmentsToOptions(classification.departments, allDepartments)
  }
}

function getClassificationWithDepartments(classification) {
  return { ...classification, departments: classification.departments || [] }
}

function RequestClassification({
  foiId,
  requestClassification,
  allKeywords,
  allDepartments = [],
  onUpdateClassification = () => {},
  onUpdateKeywords = () => {}
}) {
  const [isEditing, setIsEditing] = useState(false)
  const [classification, setClassification] = useState(() => getClassificationWithDepartments(requestClassification))
  const [keywordOptions, setKeywordOptions] = useState(allKeywords)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues: getClassificationFormValues(classification, allDepartments)
  })
  const { reset, handleSubmit } = methods

  const toggleEdit = () => setIsEditing(isEditing => !isEditing)
  const onError = (errors, e) => console.error('Error', errors, e)

  function onSubmit(payload) {
    setLoading(true)
    setError(false)

    const updatePayload = {
      ...payload,
      departments: payload.departments
    }

    axios
      .patch(`/foi/${foiId}`, updatePayload, { headers: { 'Content-Type': 'application/json' } })
      .then(({ data }) => {
        const updatedClassification = {
          applicantType: data.data.applicantType,
          keywords: data.data.keywords,
          departments: data.data.departments || []
        }

        setClassification(updatedClassification)
        onUpdateClassification(updatedClassification)
        reset(getClassificationFormValues(updatedClassification, allDepartments))
        const newKeywords = data.data.keywords.filter(a => !keywordOptions.some(b => b.id === a.id))
        const updatedKeywordOptions = [...keywordOptions, ...newKeywords]
        setKeywordOptions(updatedKeywordOptions)
        onUpdateKeywords(updatedKeywordOptions)

        toggleEdit()
        setLoading(false)
      })
      .catch(e => {
        setError(true)
        console.error('Failed to update the request classification', e)
        setLoading(false)
      })
  }

  function onCancel() {
    reset(getClassificationFormValues(classification, allDepartments))
    toggleEdit()
    setError(false)
  }

  return (
    <FormProvider {...methods}>
      <Form onSubmit={handleSubmit(onSubmit, onError)} gap="20px">
        <HeadingContainer>
          <StyledBoldFormTitle>Request Classification</StyledBoldFormTitle>
          {!isEditing ? (
            <SecondaryButton onClick={toggleEdit}>Edit</SecondaryButton>
          ) : (
            <FlexEndButtonErrorContainer>
              <StyledFlexEndContainer>
                <SecondaryButton onClick={onCancel} disabled={loading}>
                  Cancel
                </SecondaryButton>
                <Button type="submit" loading={loading}>
                  Save
                </Button>
              </StyledFlexEndContainer>
              {error && <ErrorMessage>Error updating request classification. Please try again.</ErrorMessage>}
            </FlexEndButtonErrorContainer>
          )}
        </HeadingContainer>
        <WhiteBGContainer padding="15px">
          {!isEditing ? (
            <DetailSection
              details={{
                'Applicant Type': applicantTypeMap[classification.applicantType],
                Keywords: <Keywords keywords={classification.keywords} />,
                Departments: <Departments departments={classification.departments} />
              }}
            />
          ) : (
            <FlexColumn gap="15px">
              <FormSelect
                id="applicantType"
                label="Applicant Type"
                options={applicantTypeOptions}
                defaultValue={classification.applicantType}
                required
                disabled={loading}>
                {applicantTypeOptions.map(t => (
                  <SelectMenuItem key={t.value} value={t.value}>
                    {t.label}
                  </SelectMenuItem>
                ))}
              </FormSelect>
              <AutocompleteMultiSelect id="keywords" label="Keywords" options={keywordOptions} disabled={loading} />
              <AutocompleteMultiSelect
                id="departments"
                label="Departments"
                options={allDepartments}
                defaultValue={mapDepartmentsToOptions(classification.departments, allDepartments)}
                allowCustomValues={false}
                maxVisibleOptions={5}
                disabled={loading}
              />
            </FlexColumn>
          )}
        </WhiteBGContainer>
      </Form>
    </FormProvider>
  )
}

RequestClassification.propTypes = {
  foiId: PropTypes.string.isRequired,
  requestClassification: PropTypes.shape({
    applicantType: PropTypes.oneOf(['COMMERCIAL', 'PERSONAL']).isRequired,
    keywords: PropTypes.array.isRequired,
    departments: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
      }).isRequired
    ).isRequired
  }).isRequired,
  allKeywords: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ).isRequired,
  allDepartments: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired
    })
  ),
  onUpdateClassification: PropTypes.func,
  onUpdateKeywords: PropTypes.func
}

export default RequestClassification
